// The project of a document: a chip with its name (or "Dodaj projekt"), which opens a select of the
// firm's projects; a change is saved at once (queued like every document change).
import { useId, useState } from 'react';
import type { DocumentSummary, Project } from '../../../shared/types';
import { choosableProjects, useProjects } from '../lib/projects';
import { Link } from './NavGuard';
import { Icon } from './Icon';

interface ProjectPickerProps {
  /** The document's current project (`projectName` may lag behind `projectId` in optimistic copies). */
  doc: Pick<DocumentSummary, 'projectId' | 'projectName'>;
  onChange: (projectId: string | null) => void;
  /** The chip only (no editing), e.g. for a done reminder. */
  readOnly?: boolean;
  /** What the document is called in the labels ("opomnika", "sestanka", "nadzora"). */
  noun?: string;
}

const NONE = '';

/** The current name: from the list when it is known (renamed meanwhile), else as the server sent it. */
export function projectLabel(doc: Pick<DocumentSummary, 'projectId' | 'projectName'>, projects: readonly Project[] | null): string | null {
  const id = doc.projectId ?? null;
  if (id === null) return null;
  return projects?.find((p) => p.id === id)?.name ?? doc.projectName ?? 'Projekt';
}

export function ProjectPicker({ doc, onChange, readOnly = false, noun = 'dokumenta' }: ProjectPickerProps) {
  const projects = useProjects();
  const [editing, setEditing] = useState(false);
  const selectId = useId();
  const name = projectLabel(doc, projects.data);
  const list = projects.data ?? [];
  // Nothing to pick from yet (no projects): no chip, so the lists stay quiet until projects exist.
  if (name === null && (readOnly || list.length === 0)) return null;

  if (readOnly || !editing) {
    const label = name ? `Projekt: ${name}` : `Dodaj projekt`;
    if (readOnly) {
      return (
        <span className="chip chip--project" title={label}>
          <Icon name="folder" size={14} /> {name}
        </span>
      );
    }
    return (
      <button
        type="button"
        className={`chip chip--button ${name ? 'chip--project' : 'chip--neutral'}`}
        onClick={() => setEditing(true)}
        aria-expanded={false}
        aria-label={name ? `Projekt ${name}. Spremeni projekt ${noun}.` : `Dodaj projekt ${noun}`}
      >
        <Icon name="folder" size={14} /> {name ?? 'Dodaj projekt'}
      </button>
    );
  }

  const choices = choosableProjects(list, doc.projectId);
  return (
    <div
      className="project-picker"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          setEditing(false);
        }
      }}
    >
      <label htmlFor={selectId} className="small">
        Projekt {noun}
      </label>
      <div className="project-picker__row">
        <select
          id={selectId}
          autoFocus
          value={doc.projectId ?? NONE}
          onChange={(e) => {
            onChange(e.target.value === NONE ? null : e.target.value);
            setEditing(false);
          }}
        >
          <option value={NONE}>Brez projekta</option>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.archived ? ' (arhiviran)' : ''}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn--ghost btn--small" onClick={() => setEditing(false)}>
          Prekliči
        </button>
      </div>
      <p className="muted small">
        Projekte urejate v <Link to={{ name: 'settings' }}>nastavitvah</Link>.
      </p>
    </div>
  );
}

/** The read-only chip for lists (home page). */
export function ProjectChip({ doc }: { doc: Pick<DocumentSummary, 'projectId' | 'projectName'> }) {
  const projects = useProjects();
  const name = projectLabel(doc, projects.data);
  if (name === null) return null;
  return (
    <span className="chip chip--project" title={`Projekt: ${name}`}>
      <Icon name="folder" size={14} /> {name}
    </span>
  );
}

/** "Vsi projekti" / each project / "Brez projekta" for list filters; `value` '' = all, 'none' = without. */
export function ProjectFilter({ value, onChange, id }: { value: string; onChange: (value: string) => void; id: string }) {
  const projects = useProjects();
  const list = projects.data ?? [];
  if (list.length === 0 && value === '') return null;
  const active = list.filter((p) => !p.archived || p.id === value);
  return (
    <div className="project-filter">
      <label htmlFor={id} className="sr-only">
        Filtriraj po projektu
      </label>
      <Icon name="folder" size={18} className="project-filter__icon" />
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className="project-filter__select">
        <option value="">Vsi projekti</option>
        {active.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.archived ? ' (arhiviran)' : ''}
          </option>
        ))}
        <option value="none">Brez projekta</option>
      </select>
    </div>
  );
}
